/**
 * StatsPage — `/results` (design: "Statistika / Mening natijalarim" artboard)
 *
 * Stat row (exams taken, average, total time, rank) · score-dynamics line
 * chart · recent exam history. Data from GET /api/stats/me/.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getMyStats } from '@/api/social'
import { Hex, Stat, Badge, SectionHeader } from '@/components/kit'
import { FileText, Star, Clock, Trophy, ArrowRight } from 'lucide-react'

function fmtDuration(seconds) {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return h > 0 ? `${h}s ${m}d` : `${m}d`
}

function toneFor(pct) {
  if (pct >= 85) return 'mint'
  if (pct >= 70) return 'butter'
  return 'coral'
}

/* SVG line chart of monthly average percent */
function TrendChart({ monthly }) {
  const W = 560, H = 220, P = 30
  const innerW = W - P * 2, innerH = H - P * 2
  const data = monthly.length ? monthly : [{ month: '—', average_percent: 0 }]
  const xs = (i) => P + (data.length > 1 ? (i / (data.length - 1)) * innerW : innerW / 2)
  const ys = (v) => P + innerH - (v / 100) * innerH
  const path = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xs(i)},${ys(d.average_percent)}`).join(' ')
  const last = data[data.length - 1]
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
      <defs>
        <linearGradient id="coralG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--coral)" stopOpacity="0.3" />
          <stop offset="100%" stopColor="var(--coral)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 25, 50, 75, 100].map((v) => (
        <g key={v}>
          <line x1={P} x2={W - P} y1={ys(v)} y2={ys(v)} stroke="var(--border)" strokeDasharray="4 4" />
          <text x={6} y={ys(v) + 4} fontSize="10" fill="var(--ink-3)">{v}</text>
        </g>
      ))}
      {data.map((d, i) => (
        <text key={i} x={xs(i)} y={H - 8} textAnchor="middle" fontSize="11" fill="var(--ink-3)">
          {d.month.slice(5)}
        </text>
      ))}
      {data.length > 1 && (
        <path d={`${path} L${xs(data.length - 1)},${H - P} L${xs(0)},${H - P} Z`} fill="url(#coralG)" />
      )}
      <path d={path} fill="none" stroke="var(--coral)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {data.map((d, i) => (
        <circle key={i} cx={xs(i)} cy={ys(d.average_percent)} r="4" fill="#fff" stroke="var(--coral)" strokeWidth="2" />
      ))}
      <text x={xs(data.length - 1)} y={ys(last.average_percent) - 12} textAnchor="middle"
        fontSize="11" fontWeight="700" fill="var(--coral)">{last.average_percent}</text>
    </svg>
  )
}

export default function StatsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    const controller = new AbortController()
    getMyStats({ signal: controller.signal })
      .then(({ data }) => setStats(data))
      .catch((err) => { if (err.code !== 'ERR_CANCELED') setError(err.message) })
    return () => controller.abort()
  }, [])

  if (error) return <div className="t-sm" style={{ color: 'var(--danger)' }}>{error}</div>
  if (!stats) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--coral-soft)', borderTopColor: 'var(--coral)' }} />
      </div>
    )
  }

  return (
    <div>
      <SectionHeader
        title={t('stats.title', 'Mening natijalarim')}
        subtitle={t('stats.subtitle', "Barcha imtihonlaringiz bo'yicha tahlil")}
      />

      {/* Stat row */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat icon={FileText} tone="coral" label={t('stats.examsTaken', 'Ishlangan imtihonlar')} value={stats.exams_taken} />
        <Stat icon={Star} tone="butter" label={t('stats.avgScore', "O'rtacha natija")} value={`${stats.average_percent}%`} />
        <Stat icon={Clock} tone="mint" label={t('stats.totalTime', 'Jami vaqt')} value={fmtDuration(stats.total_time_seconds)} />
        <Stat icon={Trophy} tone="lav" label={t('stats.rank', "Reyting o'rni")} value={`#${stats.rank}`} />
      </div>

      {/* Trend chart */}
      <div className="card" style={{ padding: 20, borderRadius: 22, marginBottom: 16 }}>
        <SectionHeader title={t('stats.dynamics', 'Natija dinamikasi')} />
        <TrendChart monthly={stats.monthly} />
      </div>

      {/* History */}
      <div className="card" style={{ padding: 8, borderRadius: 22 }}>
        <div style={{ padding: '12px 16px' }}>
          <div className="h-display dot" style={{ fontSize: 18 }}>{t('stats.recent', "So'nggi imtihonlar")}</div>
        </div>
        {stats.recent_results.length === 0 && (
          <div className="t-sm t-mut" style={{ padding: '14px 16px' }}>
            {t('stats.empty', "Hali imtihon ishlanmagan — birinchisini boshlang!")}
          </div>
        )}
        {stats.recent_results.map((row, i) => {
          const tone = toneFor(row.percent)
          return (
            <div key={row.result_id} className="row gap-16" style={{ padding: '14px 16px', borderTop: i ? '1px solid var(--border)' : 0 }}>
              <Hex size="sm" icon={FileText} bg={`var(--${tone}-soft)`} fg={`var(--${tone})`} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="fw-7 t-sm">{row.exam_title}</div>
                <div className="t-xs t-mut">
                  {row.finished_at ? new Date(row.finished_at).toLocaleDateString() : ''}
                </div>
              </div>
              <div className="hidden sm:block" style={{ width: 160 }}>
                <div className="prog"><i style={{ width: `${row.percent}%`, background: `var(--${tone})` }} /></div>
              </div>
              <div style={{ width: 70, textAlign: 'right' }}>
                <span className="fw-7" style={{ color: `var(--${tone})` }}>{row.score}</span>
                <span className="t-mut t-xs">/{row.max_score}</span>
              </div>
              {row.grade_label && <Badge tone={tone}>{row.grade_label}</Badge>}
              <button onClick={() => navigate(`/exam/${row.exam_code}/result`)} className="btn btn-ghost" style={{ padding: 6 }}>
                <ArrowRight size={14} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
